import { Vector3 } from "three";
import type { Engine } from "../../../../engine/Engine";
import type { PointerGesturePressDetail } from "../../../../input/PointerGestureInterpreter";
import type { PlanarGraphController } from "../../../../shape/segment/PlanarGraphController";
import type { TargetCandidate } from "../TargetCandidate";
import { resolveDrawSnapZoom } from "../../../../core/scene/drawSnapSettings";
import { pointerXzOnFloor } from "../../segment/tools/planePointer";
import type { TargetPicker } from "../../segment/interaction/pick/TargetPicker";
import type { SegmentInteractionTarget } from "../../segment/interaction/DesignerTarget";
import type { DesignerObject } from "../../object/DesignerObject";
import type { SnapKind } from "../../../../snap/SnapEngine";
import type { XzPoint } from "../../../../shape/segment/types";

export class UnifiedSegmentTargetPicker {
  constructor(
    private readonly _segments: PlanarGraphController,
    private readonly _picker: TargetPicker,
    private readonly _engine: Engine,
    private readonly _createObject: (
      target: SegmentInteractionTarget,
      raw: XzPoint,
      snapped: XzPoint,
      snappedKinds: SnapKind[],
    ) => DesignerObject,
  ) {}

  pick(event: PointerGesturePressDetail): TargetCandidate | null {
    const raw = pointerXzOnFloor(event as any, this._engine);
    const result = this._picker.pick({
      point: raw,
      zoom: resolveDrawSnapZoom(this._engine),
      planarGraph: this._segments.planarGraph,
    });
    if (!result) return null;
    const world = new Vector3(result.snapped.x, 0, result.snapped.z);
    const projected = world.project(this._engine.activeCamera!.instance);
    const snappedKinds = result.snapKind ? [result.snapKind] : [];
    return {
      object: this._createObject(result.target, raw, result.snapped, snappedKinds),
      depth: projected.z,
      screenDistance: Math.hypot(result.snapped.x - raw.x, result.snapped.z - raw.z),
      priority:
        (result.target.part === "endpoint" || result.target.part === "thickness")
          ? 2
          : 1,
      selectOnly: result.selectOnly,
      raw,
      snapped: result.snapped,
      snappedKinds,
    };
  }
}
